import { Fragment, FC, useEffect, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { CommonStyle, Loader } from "Components";
import { SideModalProps } from "DataTypes/Modal";
import TabPanel from "Components/TabPanel";
import ImgPath from "Constant/Imgs";
import RedoxApi from "Service/Redox.api";
import RedoxFormatter from "Utils/Redox.Formatter";
import { MedSearchType } from "DataTypes/Services/Redox.type";
import { PatientInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { label } from "Constant/En";
import DateTime from "Utils/DateTime";
import NoData from "Components/NoData";
import AddEditMeds from "./AddEditMeds";

const a11yProps: (index: number) => {
  id: string,
  "aria-controls": string
} = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

export interface DataActionType {
  data: MedSearchType | null;
  action: "ADD" | "EDIT" | "VIEW" | "DELETE" | null;
}

const initailState: DataActionType = { data: null, action: null }

const Meds: FC<SideModalProps> = ({ onClose }) => {
  const { patientInfo }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const crudPermission = myInfo.roleId != 6;
  const [medsList, setMedsList] = useState<MedSearchType[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [dataAction, setDataAction] = useState<DataActionType>(initailState);

  useEffect(() => {
    getMedsData();
    setDataAction(initailState)
  }, [tabValue]);

  const getMedsData: () => void = async () => {
    setLoading(true);
    const payload = { medicationsActive: tabValue == 0 ? "active" : "history" };
    const res: any = await RedoxApi.MedSearch({
      id1: patientInfo.id,
      data: payload,
    });
    if (res?.success) {
      const formatedData = RedoxFormatter.MedSearch(res?.data?.medications);
      setMedsList(formatedData);
    } else setMedsList([]);
    setLoading(false);
  };

  const handleCloseLayer2: () => void = () => {
    setDataAction(initailState)
  };

  const displayMedsHead: (data: MedSearchType) => JSX.Element = (data) => {
    return (
      <Box sx={{ ...sxMedBox }}>
        <Typography variant="subtitle1" sx={{ ...sxMedName }}>
          {data.productName || label.naText}
        </Typography>
        {crudPermission && (
          <Box display="flex" sx={{ "&>div": { ml: 1 } }} width="15%">
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "EDIT" })}
            >
              <ImgPath.EditOutlined />
            </Box>
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "DELETE" })}
            >
              <ImgPath.DeleteOutline />
            </Box>
          </Box>
        )}
      </Box>
    )
  }

  const HistoricalUIContent = (
    <Box sx={{ ...sxMain }}>
      {loading ? (
        <Loader />
      ) : medsList.length ? (
        medsList.map((el) => {
          return (
            <Box key={el.id} sx={{ ...sxContainer }}>
              {displayMedsHead(el)}
              <Typography variant="subtitle1">
                Stop Date: {DateTime.ToLocalDate(el.endDate) || label.naText}
              </Typography>
            </Box>
          );
        })
      ) : (
        <NoData />
      )}
    </Box>
  );

  const ActiveUIContent = (
    <Box sx={{ ...sxMain }}>
      {loading ? (
        <Loader />
      ) : medsList.length ? (
        medsList.map((el) => {
          return (
            <Box key={el.id} sx={{ ...sxContainer }}>
              {displayMedsHead(el)}
              <Box display="flex" justifyContent="space-between">
                <Typography variant={"subtitle1"} sx={{ ...sxMedDetails }}>
                  Route: {el.routeName || label.naText}
                </Typography>
                <Typography variant={"subtitle1"} sx={{ ...sxMedDetails }}>
                  Dose:{" "}
                  {`${(el.doseQuantity + " " + el.doseUnits).trim() ||
                    label.naText}`}
                </Typography>
                <Typography variant={"subtitle1"} sx={{ ...sxMedDetails }}>
                  Frequency:{" "}
                  {`${(el.frequencyPeriod + " " + el.frequencyUnit).trim() ||
                    label.naText}`}
                </Typography>
              </Box>
            </Box>
          );
        })
      ) : (
        <NoData />
      )}
    </Box>
  );

  const Layer1 = (
    <Fragment>
      <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
        <Typography fontWeight="bold">Medications</Typography>
        <Box display={"flex"}>
          {
            crudPermission && (
              <Box
                sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
                onClick={() => setDataAction({ data: null, action: "ADD" })}
              >
                <ImgPath.PlusIcon titleAccess="Add Medication" />
              </Box>
            )
          }
          <Box
            sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
            onClick={() => {
              handleCloseLayer2();
              onClose();
            }}
          >
            <ImgPath.CloseIcon titleAccess="Close" />
          </Box>
        </Box>
      </Box>
      <Box sx={{ ...sxTab }}>
        <Tabs
          value={tabValue}
          onChange={(event, index) => {
            setTabValue(index);
          }}
          aria-label="basic tabs example"
          variant="fullWidth"
          indicatorColor="secondary"
        >
          <Tab label="Active" {...a11yProps(0)} />
          <Tab label="Historical" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0} style={{ ...sxTabPanel }}>
        {ActiveUIContent}
      </TabPanel>
      <TabPanel value={tabValue} index={1} style={{ ...sxTabPanel }}>
        {HistoricalUIContent}
      </TabPanel>
    </Fragment>
  );

  return (
    <Fragment>
      <Box width="40%">{Layer1}</Box>
      <Box width="1%"></Box>
      {dataAction.action && (
        <Box width="54%">
          <AddEditMeds
            dataAction={dataAction}
            onClose={handleCloseLayer2}
            refreshList={getMedsData}
          />
        </Box>
      )}
    </Fragment>
  );
};
export default Meds;

const sxTab = {
  px: 2,
  "&>div": {
    borderBottom: 1,
    borderColor: "custom.main",
  },
};

const sxHeader = {
  justifyContent: "space-between",
  height: "10%",
  px: 2,
};

const sxTabPanel = {
  height: "75%",
};

const sxMain = {
  height: "100%",
  overflow: "auto",
  px: 2,
};

const sxContainer = {
  py: 1,
  borderBottom: "1px solid",
  borderBottomColor: "custom.main",
};

const sxMedBox = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
};

const sxMedName = {
  fontWeight: "bold",
  width: "80%",
  lineBreak: "anywhere"
};

const sxMedDetails = {
  width: "32%",
  lineBreak: "anywhere",
};
